import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { COMPANY_INFO } from '../data/company';
import { fetchCmsServiceBySlug, fetchCmsServices, fetchCmsGalleryBySlug } from '../lib/cms';
import ContactFormSection from '../components/ContactFormSection';
import LightboxModal from '../components/LightboxModal';

export default function ServiceDetailPage({ slug: propSlug }) {
  const params = useParams();
  const slug = propSlug || params.slug;
  const [service, setService] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  useEffect(() => {
    async function loadServiceData() {
      setLoading(true);
      const svc = await fetchCmsServiceBySlug(slug);
      const svcs = await fetchCmsServices();
      let gal = null;

      if (svc && svc.gallery_slug) {
        gal = await fetchCmsGalleryBySlug(svc.gallery_slug);
      } else {
        gal = await fetchCmsGalleryBySlug(`galleri-${slug}`);
      }

      setService(svc);
      setAllServices(svcs);
      setGallery(gal);
      setLoading(false);
    }
    loadServiceData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-20">
        <div className="flex items-center gap-3 text-sky-800 font-bold">
          <i className="fa-solid fa-spinner animate-spin text-2xl" aria-hidden="true" />
          <span>Henter ydelse...</span>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-extrabold text-slate-900 mb-4">Ydelsen blev ikke fundet</h1>
        <p className="text-slate-600 mb-8">Vi kunne desværre ikke finde den ønskede ydelse.</p>
        <Link
          to="/ydelser"
          className="inline-flex items-center gap-2 bg-sky-800 text-white font-bold py-3 px-6 rounded-xl hover:bg-sky-900 transition-colors"
        >
          <i className="fa-solid fa-arrow-left" aria-hidden="true" />
          <span>Se alle ydelser</span>
        </Link>
      </div>
    );
  }

  const primaryImg = service.primary_image?.[0] || "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/rengoering-erhverv-gulvrengoering-i-lang-gang-01.jpg";
  const galleryImages = gallery?.images || service.secondary_images || [primaryImg];

  // Filter related services
  const relatedSlugs = service.related_services ? service.related_services.split(',') : [];
  const relatedList = allServices.filter((s) => {
    const sSlug = s.slug.da || s.slug;
    return sSlug !== slug && (relatedSlugs.includes(sSlug) || relatedSlugs.length === 0);
  }).slice(0, 3);

  return (
    <>
      <Helmet>
        <title>{service.meta_title || `${service.title} · OP RENGØRINGSSERVICE`}</title>
        <meta name="description" content={service.meta_description || service.short_description || `${service.title} fra OP Rengøringsservice`} />
        <meta property="og:title" content={service.meta_title || `${service.title} · OP RENGØRINGSSERVICE`} />
        <meta property="og:description" content={service.short_description} />
        <link rel="canonical" href={`https://opservice.dk/${slug}/`} />
      </Helmet>

      {/* Hero Header */}
      <section className="bg-slate-900 text-white py-16 lg:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <img src={primaryImg} alt="" className="w-full h-full object-cover blur-sm" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">
              <Link to="/" className="hover:text-emerald-400 transition-colors">Forside</Link>
              <span>/</span>
              <Link to="/ydelser" className="hover:text-emerald-400 transition-colors">Ydelser</Link>
              <span>/</span>
              <span className="text-emerald-400">{service.title}</span>
            </nav>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-4">
              {service.title}
            </h1>

            {service.short_description && (
              <p className="text-slate-300 text-lg leading-relaxed mb-8">
                {service.short_description}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-4">
              <a
                href={`tel:${COMPANY_INFO.phoneRaw}`}
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-5 py-3 rounded-xl shadow transition-colors text-sm whitespace-nowrap-custom"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span>Ring: {COMPANY_INFO.phone}</span>
              </a>

              <a
                href="#kontakt-formular"
                className="inline-flex items-center gap-2 bg-sky-800 hover:bg-sky-700 text-white font-bold px-5 py-3 rounded-xl shadow transition-colors text-sm"
              >
                <span>Få et tilbud</span>
                <i className="fa-solid fa-arrow-down" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Content Column */}
            <div className="lg:col-span-8 space-y-8">
              <div className="prose-custom">
                <div dangerouslySetInnerHTML={{ __html: service.body_text }} />
              </div>

              {/* Primary Image */}
              <div className="rounded-3xl overflow-hidden shadow-lg border border-slate-200 bg-slate-50">
                <img
                  src={primaryImg}
                  alt={service.title}
                  className="w-full h-auto object-contain block"
                />
              </div>

              {/* Gallery attached to this service */}
              {galleryImages && galleryImages.length > 0 && (
                <div className="pt-8 border-t border-slate-100">
                  <h3 className="text-xl font-bold text-slate-900 mb-4">
                    Billeder fra {service.title}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {galleryImages.map((imgUrl, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setActiveImageIndex(idx)}
                        className="group relative rounded-2xl overflow-hidden bg-slate-100 aspect-4/3 border border-slate-200/60 shadow-xs focus:outline-none focus:ring-2 focus:ring-sky-500"
                      >
                        <img
                          src={typeof imgUrl === 'string' ? imgUrl : imgUrl.src}
                          alt={`${service.title} billede ${idx + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-slate-900/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                          <i className="fa-solid fa-magnifying-glass-plus text-lg" aria-hidden="true" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Sidebar */}
            <div className="lg:col-span-4 space-y-6">
              
              {/* Quick Contact Box */}
              <div className="bg-slate-900 text-white p-6 sm:p-8 rounded-3xl shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 -mt-8 -mr-8 w-40 h-40 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none" />
                
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-lg mb-4 border border-emerald-500/30">
                  <i className="fa-solid fa-headset" aria-hidden="true" />
                </div>

                <h3 className="text-xl font-bold text-white mb-2">Brug for hjælp?</h3>
                <p className="text-slate-300 text-sm mb-6 leading-relaxed">
                  Har du spørgsmål til vores {service.title.toLowerCase()} eller ønsker du et uforpligtende tilbud?
                </p>

                <div className="space-y-3">
                  <a
                    href={`tel:${COMPANY_INFO.phoneRaw}`}
                    className="flex items-center justify-center gap-2.5 w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 px-4 rounded-xl shadow transition-colors text-sm whitespace-nowrap-custom"
                  >
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                    <span>Ring: {COMPANY_INFO.phone}</span>
                  </a>

                  <a
                    href={`mailto:${COMPANY_INFO.email}`}
                    className="flex items-center justify-center gap-2.5 w-full bg-slate-800 hover:bg-slate-700 text-white font-semibold py-3 px-4 rounded-xl border border-slate-700 transition-colors text-sm whitespace-nowrap-custom"
                  >
                    <i className="fa-solid fa-envelope text-sky-400" aria-hidden="true" />
                    <span>Send e-mail</span>
                  </a>
                </div>
              </div>

              {/* Company Info Badge */}
              <div className="bg-slate-50 border border-slate-200/80 p-6 rounded-2xl space-y-3">
                <h4 className="font-bold text-slate-900 text-sm uppercase tracking-wider">
                  Om OP Rengøringsservice
                </h4>
                <p className="text-slate-600 text-xs leading-relaxed">
                  Vi er mere end 20 kompetente medarbejdere med fokus på tillid, grundighed og kvalitet i hele Nibe og omegn.
                </p>
                <div className="text-xs font-semibold text-emerald-700 flex items-center gap-1.5 pt-1">
                  <i className="fa-solid fa-circle-check text-emerald-500" aria-hidden="true" />
                  <span>I GO’E HÆNDER</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      {relatedList.length > 0 && (
        <section className="py-16 bg-slate-50 border-t border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <span className="text-xs font-bold text-sky-800 uppercase tracking-widest block mb-1">
                  Flere Ydelser
                </span>
                <h2 className="text-2xl font-bold text-slate-900">Relaterede serviceydelser</h2>
              </div>
              <Link to="/ydelser" className="text-sm font-bold text-sky-800 hover:text-sky-900 inline-flex items-center gap-1">
                <span>Se alle</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedList.map((rel) => (
                <Link
                  key={rel.slug.da || rel.slug}
                  to={`/ydelser/${rel.slug.da || rel.slug}`}
                  className="bg-white p-6 rounded-2xl border border-slate-200/80 hover:border-sky-300 shadow-xs hover:shadow-md transition-all flex flex-col justify-between group"
                >
                  <div>
                    <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-800 group-hover:bg-sky-800 group-hover:text-white flex items-center justify-center text-lg transition-colors mb-4">
                      <i className={rel.icon || 'fa-solid fa-sparkles'} aria-hidden="true" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 group-hover:text-sky-800 transition-colors mb-2">
                      {rel.title}
                    </h3>
                    <p className="text-slate-600 text-xs line-clamp-2 leading-relaxed">
                      {rel.short_description}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-100 text-xs font-bold text-sky-800 flex items-center justify-between">
                    <span>Læs mere</span>
                    <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Form Anchor */}
      <div id="kontakt-formular">
        <ContactFormSection title={`Få et tilbud på ${service.title.toLowerCase()}`} />
      </div>

      {/* Lightbox Modal */}
      <LightboxModal
        images={galleryImages}
        activeIndex={activeImageIndex}
        onClose={() => setActiveImageIndex(null)}
        onPrev={() => setActiveImageIndex((prev) => (prev > 0 ? prev - 1 : galleryImages.length - 1))}
        onNext={() => setActiveImageIndex((prev) => (prev < galleryImages.length - 1 ? prev + 1 : 0))}
      />
    </>
  );
}
